/**
 * Landing — public pre-auth page. Leads with the sharpened promise and shows
 * the product's actual surfaces (memory / insight / accountability) so a cold
 * visitor feels what's different before signing up, instead of hitting a bare
 * login form. Mobile-first; uses the brand display-type + gradient tokens.
 */

import { useEffect } from 'react';
import { Compass, ArrowRight, Brain, ClipboardCheck, Users, History, Eye, CalendarCheck, Check, MessageSquare, Network, Moon, Sunrise, Play, Music } from 'lucide-react';
import SamplePlayer from './SamplePlayer';
import NewsletterSignup from './NewsletterSignup';
import Footer from './Footer';
import { track } from '../lib/analytics';
import useFoundingOffer from '../lib/useFoundingOffer';
import FoundingBanner from './FoundingBanner';

function Wordmark({ size = 18 }) {
  return (
    <div className="flex items-center gap-2">
      <Compass size={size} className="text-blue-400" />
      <span className="font-display font-semibold tracking-tight bg-brand-gradient bg-clip-text text-transparent">The Navigator</span>
    </div>
  );
}

// A faithful mini-preview of the real Today surfaces.
function ProductPreview() {
  return (
    <div className="space-y-3 max-w-md mx-auto">
      {/* Continuity Strip */}
      <div className="rounded-lg border border-slate-700/50 bg-slate-800/40 shadow-soft p-4 text-left">
        <div className="flex items-center gap-2 mb-2">
          <History size={14} className="text-blue-400" />
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Where you left off</span>
          <span className="text-[11px] text-slate-600 ml-auto">3 days ago</span>
        </div>
        <blockquote className="border-l-2 border-blue-500/50 pl-3">
          <p className="text-sm text-slate-200 italic leading-snug">&ldquo;I'll send the three outreach emails before we talk again.&rdquo;</p>
          <p className="text-[11px] text-slate-500 mt-1">— what you committed to</p>
        </blockquote>
      </div>

      {/* What the Navigator sees */}
      <div className="rounded-lg border border-slate-700/50 bg-slate-800/40 shadow-soft p-4 text-left">
        <div className="flex items-center gap-2 mb-2">
          <Eye size={14} className="text-blue-400" />
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">What the Navigator sees</span>
          <span className="text-[11px] px-2 py-0.5 rounded-full border bg-amber-500/15 text-amber-300 border-amber-700/40 ml-auto">Capacity</span>
        </div>
        <p className="text-sm text-slate-200 leading-snug">You go quiet on Health whenever Work heats up — three weeks running.</p>
      </div>

      {/* Today's plan */}
      <div className="rounded-lg border border-slate-700/50 bg-slate-800/40 shadow-soft p-4 text-left">
        <div className="flex items-center gap-2 mb-3">
          <CalendarCheck size={14} className="text-blue-400" />
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Today's commitments</span>
        </div>
        <div className="space-y-2">
          {['Send the three outreach emails', 'Draft the proposal intro'].map(t => (
            <div key={t} className="flex items-center gap-2.5">
              <span className="w-4 h-4 rounded-full bg-green-500/20 border border-green-500/60 flex items-center justify-center flex-shrink-0">
                <Check size={10} className="text-green-400" />
              </span>
              <span className="text-sm text-slate-400 line-through">{t}</span>
            </div>
          ))}
        </div>
        <p className="text-sm font-medium text-green-300 mt-3">Commitments locked in. You've won the day.</p>
      </div>

      {/* Tonight's Rest */}
      <div className="rounded-lg border border-indigo-900/50 bg-slate-800/40 shadow-soft p-4 text-left">
        <div className="flex items-center gap-2 mb-3">
          <Moon size={14} className="text-indigo-400" />
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Tonight's Rest</span>
          <span className="text-[11px] text-slate-600 ml-auto">13 min</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center flex-shrink-0">
            <Play size={16} className="ml-0.5" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-200 font-medium truncate">Thursday, the three emails</p>
            <div className="mt-1.5 h-1.5 bg-slate-700/60 rounded-full overflow-hidden">
              <div className="h-full w-1/3 bg-indigo-500 rounded-full" />
            </div>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-2.5">Built from what you said today. Your voice pick, new music each night, yours to download.</p>
      </div>
    </div>
  );
}

const PILLARS = [
  { icon: Brain, title: 'Persistent memory', body: 'It remembers what you said last week, the commitments you made, and the patterns you keep repeating, so you never start from zero.' },
  { icon: ClipboardCheck, title: 'Real accountability', body: 'Every session ends with a concrete commitment and a deadline. The next one opens by checking whether you did it.' },
  { icon: Users, title: 'Outward orientation', body: 'It pushes you from preparation to delivery, toward action that creates tangible value for at least one other person.' },
  { icon: Moon, title: 'Rest that rehearses', body: 'Deep Rest turns the thing you said you would do into a guided evening rest: your words, your voice pick, new music each night. The good feeling comes from the promise, not from a streak.' },
];

const STEPS = [
  { icon: MessageSquare, title: 'Talk', body: 'Your first session is a real conversation. The Navigator learns your situation and builds your life map: a dialogue, not a questionnaire. It ends with your first Deep Rest.' },
  { icon: CalendarCheck, title: 'Plan', body: 'Each morning, define the 3 to 5 commitments that mean you have won, and the momentum builders that push you past them. Then a short Prime rehearses the plan and hands you the day.' },
  { icon: Network, title: 'Stay on it', body: 'It tracks your follow-through and patterns across every area of your life, and holds you to what you said.' },
  { icon: Moon, title: 'Rest', body: 'After the evening review, Deep Rest: 12 minutes that name what got done, rehearse tomorrow\'s first step in your own words, and leave you calm and certain. Never the same session twice.' },
];

export default function Landing({ onGetStarted, onSignIn, onPricing, onAbout, onTerms, onPrivacy, onMemory }) {
  const offer = useFoundingOffer();
  useEffect(() => {
    track('landing_view');
  }, []);

  return (
    <div className="min-h-full">
      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-slate-950/85 backdrop-blur border-b border-slate-800/60 px-safe-4 pt-safe-3 pb-3 flex items-center">
        <Wordmark />
        <button
          onClick={onSignIn}
          className="ml-auto text-sm text-slate-300 hover:text-white min-h-tap px-3 transition-colors"
        >
          Sign in
        </button>
      </header>

      {/* Hero */}
      <section className="px-4 pt-12 pb-14 text-center max-w-2xl mx-auto">
        <p className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-indigo-300 bg-indigo-900/30 border border-indigo-800/50 rounded-full px-3 py-1 mb-5">
          <Moon size={12} /> New: Deep Rest, morning and evening
        </p>
        <h1 className="text-display sm:text-display-lg font-display font-bold tracking-tight text-slate-100 leading-[1.1]">
          Keep your word to yourself.
        </h1>
        <p className="text-base text-slate-400 mt-5 max-w-xl mx-auto leading-relaxed">
          A coach that remembers what you said and holds you to it.
          And <span className="text-slate-200">Deep Rest</span>: a morning Prime and an evening Rest that
          reset your state, so following through becomes the easy thing. Follow-through is the whole game. This is built for it.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
          <button
            onClick={() => { track('cta_click', { cta: 'hero' }); onGetStarted(); }}
            className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-6 py-3 text-sm font-medium min-h-tap shadow-glow transition-colors"
          >
            Get started free <ArrowRight size={16} />
          </button>
          <button
            onClick={onPricing}
            className="inline-flex items-center justify-center bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-lg px-6 py-3 text-sm font-medium min-h-tap transition-colors"
          >
            See plans
          </button>
        </div>
        <SamplePlayer className="max-w-md mx-auto mt-8" />
        <FoundingBanner offer={offer} className="max-w-md mx-auto mt-6 text-left" />
        <p className="text-[11px] text-slate-600 mt-5 uppercase tracking-[0.2em]">Clarity · Structure · Accountability · Rest</p>
      </section>

      {/* Thesis */}
      <section className="px-4 pb-14 max-w-2xl mx-auto text-center">
        <p className="text-lg text-slate-300 leading-relaxed">
          Most people navigating change don't need more information.
          <span className="text-slate-500"> They need structured accountability, a push toward action, and a way to feel good about the day that doesn't come from a screen. Not generic advice that could apply to anyone.</span>
        </p>
      </section>

      {/* Product preview */}
      <section className="px-4 pb-16 max-w-3xl mx-auto">
        <h2 className="text-xl font-display font-semibold tracking-tight text-slate-200 text-center mb-2">What it's actually like</h2>
        <p className="text-sm text-slate-500 text-center mb-7 max-w-md mx-auto">
          It opens every day already knowing where you left off, and closes it with a rest built from what you did.
        </p>
        <ProductPreview />
      </section>

      {/* Deep Rest */}
      <section className="px-4 pb-16 max-w-3xl mx-auto">
        <div className="rounded-2xl border border-indigo-900/50 bg-gradient-to-b from-indigo-950/40 to-slate-900/30 shadow-soft px-6 py-9">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Moon size={18} className="text-indigo-400" />
            <h2 className="text-xl font-display font-semibold tracking-tight text-slate-100">Deep Rest</h2>
          </div>
          <p className="text-sm text-slate-400 text-center max-w-lg mx-auto leading-relaxed">
            Accountability apps are all stick. This is the carrot, and it is made from the same promise.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-7 text-left">
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
              <Sunrise size={18} className="text-amber-300 mb-2" />
              <h3 className="text-sm font-semibold text-slate-200 mb-1">Morning Prime</h3>
              <p className="text-xs text-slate-400 leading-relaxed">Right after you plan: six minutes that rehearse today's first action, the excuse most likely to show up, and what you do when it does.</p>
            </div>
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
              <Moon size={18} className="text-indigo-300 mb-2" />
              <h3 className="text-sm font-semibold text-slate-200 mb-1">Evening Rest</h3>
              <p className="text-xs text-slate-400 leading-relaxed">After the review: what got done, named once. What didn't, without a ledger. Tomorrow's first step, rehearsed until it feels decided. Then sleep.</p>
            </div>
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
              <Music size={18} className="text-emerald-300 mb-2" />
              <h3 className="text-sm font-semibold text-slate-200 mb-1">Never the same twice</h3>
              <p className="text-xs text-slate-400 leading-relaxed">Sixty ways in, a bank of original ambient music, your choice of voice, sessions for one life area at a time. Read every word, keep the ones you love, download for the plane.</p>
            </div>
          </div>
          <p className="text-xs text-slate-500 text-center max-w-lg mx-auto leading-relaxed mt-6">
            It works because it is not positive thinking. It is the same mental contrasting the research behind this product is built on: picture the outcome, name the obstacle, rehearse the plan. In a state where it lands.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="px-4 pb-16 max-w-4xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PILLARS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-xl border border-slate-700/50 bg-slate-800/30 shadow-soft p-5">
              <Icon size={20} className="text-blue-400 mb-3" />
              <h3 className="text-sm font-semibold text-slate-200 mb-1.5">{title}</h3>
              <p className="text-xs text-slate-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="px-4 pb-16 max-w-3xl mx-auto">
        <h2 className="text-xl font-display font-semibold tracking-tight text-slate-200 text-center mb-7">How it works</h2>
        <div className="space-y-5">
          {STEPS.map(({ icon: Icon, title, body }, i) => (
            <div key={title} className="flex gap-4 items-start">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
                <Icon size={16} className="text-blue-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-200">
                  <span className="text-slate-600 mr-1.5">{i + 1}.</span>{title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed mt-0.5">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Follow-Through Letter capture */}
      <section className="px-4 pb-16 max-w-2xl mx-auto text-center">
        <div className="rounded-2xl border border-slate-700/50 bg-slate-800/30 shadow-soft px-6 py-9">
          <h2 className="text-xl font-display font-semibold tracking-tight text-slate-100">The Follow-Through Letter</h2>
          <p className="text-sm text-slate-400 mt-2.5 mb-6 max-w-md mx-auto leading-relaxed">
            One short email a week about why the plan you make on Sunday rarely survives
            to Friday, and what it actually takes to keep your word to yourself.
          </p>
          <NewsletterSignup source="landing_letter" />
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 pb-16 max-w-2xl mx-auto text-center">
        <div className="rounded-2xl border border-slate-700/50 bg-gradient-to-b from-slate-800/50 to-slate-900/30 shadow-soft px-6 py-10">
          <h2 className="text-2xl font-display font-bold tracking-tight text-slate-100">Ready to stop letting yourself off the hook?</h2>
          <p className="text-sm text-slate-400 mt-3 max-w-md mx-auto">A mentor who remembers what you committed to and evaluates outcomes, not effort. And every night, a rest that makes keeping your word feel as good as it should.</p>
          <button
            onClick={() => { track('cta_click', { cta: 'final' }); onGetStarted(); }}
            className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-6 py-3 text-sm font-medium min-h-tap shadow-glow transition-colors mt-6"
          >
            Get started free <ArrowRight size={16} />
          </button>
          <p className="text-xs text-slate-500 mt-4">
            Already have an account?{' '}
            <button onClick={onSignIn} className="text-blue-400 hover:text-blue-300 transition-colors">Sign in</button>
          </p>
        </div>
      </section>

      {/* Footer links */}
      <Footer onAbout={onAbout} onPricing={onPricing} onMemory={onMemory} onTerms={onTerms} onPrivacy={onPrivacy} />
    </div>
  );
}
